import React, { useState } from "react";
import { Container, Box, CircularProgress, Snackbar, Alert } from "@mui/material";

import CheckPP from "../components/CheckPP";
import CheckModal from "../components/CheckModal";
import ComparisonTable from "../components/ComparisonTable";
import Seo from "../components/Seo";
import { trackEvent } from "../utils/analytics";

import API from "../api/api";

function CheckPPPage() {
    const [analysisData, setAnalysisData] = useState(null);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [comparisonData, setComparisonData] = useState([]);
    const [totals, setTotals] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (msg, severity = "info") => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const resetBeforeAnalyze = () => {
        setAnalysisData(null);
        setComparisonData([]);
        setTotals(null);
    };

    const handleCompare = async () => {
        if (!analysisData || !analysisData.files_ready || analysisData.files_ready.length === 0) {
            showSnackbar("Нет файлов для сравнения", "error");
            return;
        }

        if (compareLoading) {
            showSnackbar("Сравнение уже выполняется", "warning");
            return;
        }

        const formData = new FormData();

        const banksMap = {};
        analysisData.files_ready.forEach((f) => {
            if (!f.fileObj || f.bank === "unknown") return;

            if (!banksMap[f.bank]) banksMap[f.bank] = [];
            banksMap[f.bank].push(f.fileObj);
        });

        Object.entries(banksMap).forEach(([bank, files], index) => {
            formData.append(`banks[${index}][bank]`, bank);
            files.forEach((f) => formData.append(`banks[${index}][files]`, f));
        });

        formData.append("excel_file", analysisData.excelFile);

        setCompareLoading(true);
        setComparisonData([]);
        setTotals(null);
        setModalOpen(false);

        trackEvent("compare_accounting_started");

        try {
            const response = await API.post("/checker/compare-pp/", formData);
            const data = response.data;

            setComparisonData(data.rows || []);
            setTotals(data.totals || null);

            trackEvent("compare_accounting_finished", {
                rows_count: (data.rows || []).length,
            });

            showSnackbar("Сравнение завершено", "success");
        } catch (error) {
            console.error(error);

            const message =
                error.response?.data?.error ||
                "Ошибка соединения";

            showSnackbar(message, "error");
        } finally {
            setCompareLoading(false);
        }
    };

    return (
        <>
            <Seo
                title="Сверка платежных поручений с бухгалтерским учетом – Qarzhy"
                description="Автоматизированная сверка банковских платежных поручений с данными бухгалтерского учета. Сопоставление ИИН, ФИО, счетов, сумм и выявление расхождений."
                keywords="сверка платежных поручений с бухгалтерским учетом, сверка банка и учета, сверка платежек с учетом, сравнение банковских выплат с учетом, расхождения по платежным поручениям, сверка ИИН и сумм"
                url="https://qarzhy.kz/check-pp"
            />

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    mt: 4,
                    px: { xs: 2, sm: 3, md: 4, lg: 5 },
                    width: "100%",
                }}
            >
                <CheckPP
                    setAnalysisData={setAnalysisData}
                    setAnalyzeLoading={setAnalyzeLoading}
                    setModalOpen={setModalOpen}
                    setLoading={setLoading}
                    loading={loading}
                    compareLoading={compareLoading}
                    resetBeforeAnalyze={resetBeforeAnalyze}
                />

                <Box
                    sx={{
                        mt: 5,
                        width: "100%",
                    }}
                >
                    {compareLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : (
                        <ComparisonTable data={comparisonData} totals={totals} />
                    )}
                </Box>

                <CheckModal
                    open={modalOpen}
                    onClose={() => {
                        if (analyzeLoading) return;
                        setModalOpen(false);
                    }}
                    analysisData={analysisData}
                    analyzeLoading={analyzeLoading}
                    onCompare={handleCompare}
                    compareLoading={compareLoading}
                />

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                        "&.MuiSnackbar-root": {
                            top: "72px",
                        },
                    }}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarSeverity}
                        variant="filled"
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}

export default CheckPPPage;